import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import { cva } from "class-variance-authority";
import { AiSparkles } from "../icon/ai-sparkles";

type UserAvatarVariant = "internal" | "external";
type AvatarVariant = UserAvatarVariant | "ai";
type AvatarSize = "md" | "xs";
type AvatarRootProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Root
>;
type AvatarBaseProps = Omit<
  AvatarRootProps,
  "aria-hidden" | "aria-label" | "children" | "className" | "role"
>;
type AvatarProps =
  | (AvatarBaseProps & {
      variant: UserAvatarVariant;
      initials: string;
      "aria-label": string;
      size?: "md";
      src?: string;
    })
  | (AvatarBaseProps & {
      variant: UserAvatarVariant;
      initials: string;
      "aria-label"?: never;
      size: "xs";
      src?: string;
    })
  | (AvatarBaseProps & {
      variant: "ai";
      initials?: never;
      src?: never;
      "aria-label"?: string;
      size?: "md" | "xs";
    });

const avatarVariants = cva(
  [
    "inline-flex",
    "shrink-0",
    "items-center",
    "justify-center",
    "overflow-hidden",
    "rounded-full",
    "font-(family-name:--font-family-label)",
    "font-(number:--font-weight-medium)",
    "select-none",
  ],
  {
    variants: {
      size: {
        md: [
          "size-(--spacing-40)",
          "text-(length:--font-size-title-md)",
          "leading-(--font-line-height-24)",
        ],
        xs: [
          "size-(--spacing-20)",
          "text-(length:--font-size-label-xs)",
          "font-(number:--font-weight-regular)",
          "leading-(--font-line-height-16)",
        ],
      },
      variant: {
        internal: [
          "bg-surface-inverse-surface",
          "text-surface-inverse-on-surface",
        ],
        external: ["bg-surface-container-highest", "text-surface-on-surface"],
        ai: ["bg-ai-avatar-background"],
      },
    },
  },
);

/**
 * Circular identity mark shown as initials, a profile image, or an AI sparkles icon.
 *
 * @example
 * <Avatar aria-label="Jane Doe" initials="JD" variant="internal" />
 * <Avatar aria-label="María García" initials="MG" variant="external" />
 * <Avatar initials="JD" size="xs" variant="internal" />
 * <Avatar variant="ai" aria-label="AI" />
 */
function Avatar({
  size = "md",
  variant,
  "aria-label": ariaLabel,
  ...props
}: AvatarProps) {
  if (variant === "ai") {
    const isHidden = size === "xs";

    return (
      <AvatarPrimitive.Root
        {...props}
        data-slot="avatar"
        data-variant="ai"
        role={isHidden ? undefined : "img"}
        aria-label={isHidden ? undefined : (ariaLabel ?? "AI")}
        aria-hidden={isHidden || undefined}
        className={avatarVariants({ size, variant })}
      >
        <AiSparkles
          size={size === "xs" ? "xs" : "sm"}
          className={size === "xs" ? "size-[12px]" : undefined}
        />
      </AvatarPrimitive.Root>
    );
  }

  const { initials, src, ...rootProps } = props as AvatarBaseProps & {
    initials: string;
    src?: string;
  };
  const isHidden = size === "xs";
  const rootLabel = !src && !isHidden ? ariaLabel : undefined;
  const fallbackLabel = src && !isHidden ? ariaLabel : undefined;

  return (
    <AvatarPrimitive.Root
      {...rootProps}
      data-slot="avatar"
      data-variant={variant}
      role={rootLabel ? "img" : undefined}
      aria-label={rootLabel}
      aria-hidden={isHidden || undefined}
      className={avatarVariants({ size, variant })}
    >
      {src ? (
        <AvatarPrimitive.Image
          data-slot="avatar-image"
          src={src}
          alt={isHidden ? "" : (ariaLabel ?? "")}
          className="h-full w-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        data-slot="avatar-fallback"
        role={fallbackLabel ? "img" : undefined}
        aria-label={fallbackLabel}
      >
        <span aria-hidden="true">{initials}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
export type { AvatarProps, AvatarSize, AvatarVariant, UserAvatarVariant };
